import React, { useEffect, useState } from "react";
import { Download, Eye, Sparkles, Link2, Presentation } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClientBrandingPanel = ({ clientId }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/clients/${clientId}/branding`).then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const setAccess = async (on) => {
    setBusy(true);
    try {
      await api.put(`/clients/${clientId}`, { branding_access: on });
      toast.success(on
        ? "Branding-Dashboard freigeschaltet – der Teilnehmer sieht es ab sofort in seinem Portal"
        : "Zugang entzogen – das Dashboard ist im Portal nicht mehr sichtbar");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  if (!d) return null;
  if (!d.access) {
    return (
      <div className="border border-slate-200 p-5" data-testid="akte-branding-locked">
        <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Marken-Guide (Selbstvermarktung)</div>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Noch nicht freigeschaltet. Mit einem Klick bekommt der Teilnehmer in seinem Portal die interaktive
          Marken-Präsentation: Fragen beantworten, Bilder und Logo hochladen, Farben wählen – am Ende entsteht
          sein persönlicher Marken-Guide als PDF.
        </p>
        <button onClick={() => setAccess(true)} disabled={busy} data-testid="akte-branding-enable"
          className="mt-4 px-4 py-2.5 text-xs font-medium bg-slate-900 text-white hover:bg-black disabled:opacity-50 flex items-center gap-1.5">
          <Sparkles size={12} strokeWidth={1.5} /> {busy ? "Wird freigeschaltet…" : "Branding-Dashboard freischalten"}
        </button>
      </div>
    );
  }

  const openPdf = async () => {
    try {
      const r = await api.get(`/clients/${clientId}/branding/pdf`, { responseType: "blob", timeout: 240000 });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank", "noopener");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const answered = d.answered || 0;
  const colors = (d.colors || []).filter(Boolean);

  return (
    <div className="border border-slate-200 p-5" data-testid="akte-branding-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Marken-Guide (Selbstvermarktung)</div>
          <div className="text-sm text-slate-700 mt-1">
            {answered} von {d.steps} Schritten ausgefüllt · {d.asset_count || 0} Bilder
            {d.palette ? ` · Palette ${d.palette}` : ""}
            {d.generated_at ? " · Guide erstellt" : " · Guide noch nicht erstellt"}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {d.share_link && (
            <a href={d.share_link} target="_blank" rel="noreferrer" data-testid="akte-branding-share"
              className="px-3 py-2 text-xs border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5">
              <Link2 size={12} strokeWidth={1.5} /> Online-Guide
            </a>
          )}
          <a href={`/mein-branding?client_id=${clientId}`} data-testid="akte-branding-open"
            className="px-3 py-2 text-xs border border-slate-900 text-slate-900 hover:bg-slate-100 flex items-center gap-1.5">
            <Presentation size={12} strokeWidth={1.5} /> Präsentation öffnen
          </a>
          <button onClick={() => setAccess(false)} disabled={busy} data-testid="akte-branding-disable"
            className="px-3 py-2 text-xs border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            title="Zugang entziehen – der Teilnehmer sieht das Dashboard dann nicht mehr">
            Zugang entziehen
          </button>
          {answered > 0 && (
            <button onClick={openPdf} data-testid="akte-branding-pdf"
              className="px-3 py-2 text-xs bg-slate-900 text-white hover:bg-black flex items-center gap-1.5">
              <Download size={12} strokeWidth={1.5} /> Marken-Guide PDF
            </button>
          )}
        </div>
      </div>

      <div className="h-[3px] bg-slate-200 mt-4">
        <div className="h-full bg-slate-900" style={{ width: `${(answered / (d.steps || 1)) * 100}%` }} />
      </div>

      {colors.length > 0 && (
        <div className="flex gap-1.5 mt-4" data-testid="akte-branding-colors">
          {colors.map((c) => (
            <span key={c} className="h-7 w-7 border border-slate-200" style={{ background: c }} title={c} />
          ))}
        </div>
      )}

      {(d.assets || []).length > 0 && (
        <div className="flex gap-2 mt-4 flex-wrap">
          {d.assets.map((x) => (
            <img key={x.asset_id} src={`${API}/branding/asset/${x.asset_id}`} alt={x.kind}
              title={`${x.kind}${x.label ? ` · ${x.label}` : ""}`}
              className="h-16 w-16 object-cover border border-slate-200" data-testid={`akte-branding-asset-${x.asset_id}`} />
          ))}
        </div>
      )}

      {answered > 0 && (
        <details className="mt-5 group" data-testid="akte-branding-answers">
          <summary className="cursor-pointer text-xs font-medium text-slate-700 hover:text-slate-900 flex items-center gap-1.5">
            <Eye size={12} strokeWidth={1.5} /> Antworten des Teilnehmers anzeigen
          </summary>
          <div className="mt-4 space-y-4">
            {(d.questions || []).map((q) => {
              const v = (d.answers || {})[q.key];
              const text = Array.isArray(v) ? v.join(", ") : v;
              if (!text) return null;
              return (
                <div key={q.key}>
                  <div className="text-[11px] text-slate-500">{q.chapter} · {q.question}</div>
                  <div className="text-sm text-slate-900 mt-0.5 whitespace-pre-line">{text}</div>
                </div>
              );
            })}
            {d.ai && (
              <div className="border-t border-slate-200 pt-4">
                <div className="text-[11px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1.5">
                  <Sparkles size={11} strokeWidth={1.5} /> Daraus erzeugter Guide
                </div>
                {[["Claim", d.ai.tagline], ["Vision", d.ai.vision], ["Positionierung", d.ai.positioning],
                  ["Markenstimme", d.ai.voice_description],
                  ["Schriften", d.ai.typography ? `${d.ai.typography.heading} · ${d.ai.typography.body}` : ""]].map(([label, val]) =>
                    val ? (
                      <div key={label} className="mt-3">
                        <div className="text-[11px] text-slate-500">{label}</div>
                        <div className="text-sm text-slate-900">{val}</div>
                      </div>
                    ) : null,
                )}
              </div>
            )}
            {d.social && (
              <div className="border-t border-slate-200 pt-4" data-testid="akte-branding-social">
                <div className="text-[11px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1.5">
                  <Sparkles size={11} strokeWidth={1.5} /> Social-Media-Texte
                </div>
                {[["LinkedIn · Profilslogan", d.social.linkedin_headline], ["LinkedIn · Info", d.social.linkedin_about],
                  ["Instagram · Bio", d.social.instagram_bio], ["Vorstellungspost", d.social.intro_post]].map(([label, val]) =>
                    val ? (
                      <div key={label} className="mt-3">
                        <div className="text-[11px] text-slate-500">{label}</div>
                        <div className="text-sm text-slate-900 whitespace-pre-line">{val}</div>
                      </div>
                    ) : null,
                )}
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
};

export default ClientBrandingPanel;
